import type { Project } from '../db/models'
import { parseProjectIcon } from './projectIcon'
import { isIconColorKey } from './titlePalette'

/**
 * Der Klartext eines gestalteten Titels (ADR 0030): `name` entsteht daraus.
 * Absätze werden zu einem Leerzeichen, Mehrfach-Leerraum wird eins.
 */
export function plainTextFromTitleHtml(html: string): string {
  if (!html) return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const blocks = Array.from(doc.body.querySelectorAll('p'))
  const parts = blocks.length ? blocks.map(p => p.textContent ?? '') : [doc.body.textContent ?? '']
  return parts.join(' ').replace(/\s+/g, ' ').trim()
}

/**
 * **`name` ist nie die Quelle** (ADR 0030). Ändert eine Stelle nur `name` —
 * Umbenennen aus der Sidebar, Import, ein älterer Client —, passt `nameHtml`
 * nicht mehr dazu und fällt weg. Die Formatierung geht dann verloren, der
 * Text stimmt wieder.
 */
export function titleConsistencyChanges(
  current: Pick<Project, 'name'> | undefined,
  updates: { name?: string; nameHtml?: string | null },
): { nameHtml?: null } {
  if (updates.name === undefined || 'nameHtml' in updates) return {}
  if (current && current.name === updates.name) return {}
  return { nameHtml: null }
}

/**
 * Die **Farbe eines gestalteten Titels** für die Sidebar (gewünscht
 * 2026-09-26): die des ersten Worts. Ein Titel kann mehrere Farben tragen;
 * das erste Wort ist, was man vom Namen zuerst liest. `null` = keine eigene
 * Farbe (dann gilt die der Sidebar).
 */
export function titleColorOf(html: string | null | undefined): string | null {
  if (!html) return null
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node && !node.textContent?.trim()) node = walker.nextNode()
  const colored = node?.parentElement?.closest('[data-color]:not(mark)')
  return colored?.getAttribute('data-color') ?? null
}

/**
 * Die Farbe für Pfeil vorn und Fortschritt hinten in der Seitenleiste (#207): die des Icons, wenn
 * es eine zeigt — der Pfeil nimmt beim Überfahren seinen Platz ein —, sonst die des Titels. Nur
 * ein Lucide-Icon ist gefärbt; Emoji und Happy-Emoji bringen ihre eigenen Farben mit.
 */
export function projectAccentColor(project: Pick<Project, 'icon' | 'iconColor' | 'nameHtml'>): string | null {
  if (parseProjectIcon(project.icon)?.kind === 'lucide' && isIconColorKey(project.iconColor)) return project.iconColor
  return titleColorOf(project.nameHtml)
}
